import { useEffect, useState } from 'react'

export type Theme = 'system' | 'light' | 'dark'

const KEY = 'hauswerk.theme'

function read(): Theme {
  try {
    const value = localStorage.getItem(KEY)
    return value === 'light' || value === 'dark' ? value : 'system'
  } catch {
    return 'system'
  }
}

/**
 * Writes the choice onto <html> so CSS can win in both directions: without the
 * attribute the media query decides, with it the user's choice does.
 */
function apply(theme: Theme): void {
  const root = document.documentElement
  if (theme === 'system') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', theme)

  // Keep the mobile browser chrome in step with the app's header colour.
  const dark = theme === 'dark' || (theme === 'system' && matchMedia('(prefers-color-scheme: dark)').matches)
  // Must match --header-bg, or the browser chrome and the app header disagree.
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', dark ? '#163a2b' : '#1c6b4a')
}

/** Applied before React renders so there is no flash of the wrong theme. */
export function initTheme(): void {
  apply(read())
}

export function useTheme(): [Theme, (next: Theme) => void] {
  const [theme, setTheme] = useState<Theme>(read)

  useEffect(() => {
    apply(theme)
    try {
      if (theme === 'system') localStorage.removeItem(KEY)
      else localStorage.setItem(KEY, theme)
    } catch {
      /* storage disabled, the choice simply does not persist */
    }
  }, [theme])

  // Follow the OS while the user has not made an explicit choice.
  useEffect(() => {
    if (theme !== 'system') return
    const media = matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => apply('system')
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [theme])

  return [theme, setTheme]
}

const SUN = (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 17a5 5 0 1 1 0-10 5 5 0 0 1 0 10Zm0-13.5a1 1 0 0 1 1 1V6a1 1 0 1 1-2 0V4.5a1 1 0 0 1 1-1Zm0 15a1 1 0 0 1 1 1V21a1 1 0 1 1-2 0v-1.5a1 1 0 0 1 1-1ZM3.5 12a1 1 0 0 1 1-1H6a1 1 0 1 1 0 2H4.5a1 1 0 0 1-1-1Zm14.5 0a1 1 0 0 1 1-1h1.5a1 1 0 1 1 0 2H19a1 1 0 0 1-1-1ZM5.6 5.6a1 1 0 0 1 1.4 0l1 1a1 1 0 0 1-1.4 1.4l-1-1a1 1 0 0 1 0-1.4Zm10.4 10.4a1 1 0 0 1 1.4 0l1 1a1 1 0 0 1-1.4 1.4l-1-1a1 1 0 0 1 0-1.4Zm2.4-10.4a1 1 0 0 1 0 1.4l-1 1A1 1 0 0 1 16 6.6l1-1a1 1 0 0 1 1.4 0ZM8 16a1 1 0 0 1 0 1.4l-1 1A1 1 0 0 1 5.6 17l1-1a1 1 0 0 1 1.4 0Z" />
  </svg>
)
const MOON = (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.7 14.3A8.6 8.6 0 0 1 9.7 3.3a9 9 0 1 0 11 11Z" />
  </svg>
)
const AUTO = (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2a10 10 0 1 0 0 20V2Zm0 18a8 8 0 0 1 0-16v16Z" />
  </svg>
)

const LABEL: Record<Theme, string> = { system: 'Automatisch', light: 'Hell', dark: 'Dunkel' }
const NEXT: Record<Theme, Theme> = { system: 'light', light: 'dark', dark: 'system' }

export function ThemeToggle({
  className = 'theme-toggle',
  labels = LABEL,
}: {
  className?: string
  /** The worker app passes translated labels; the backoffice keeps German. */
  labels?: Record<Theme, string>
}) {
  const [theme, setTheme] = useTheme()
  const icon = theme === 'dark' ? MOON : theme === 'light' ? SUN : AUTO

  return (
    <button
      type="button"
      className={className}
      onClick={() => setTheme(NEXT[theme])}
      title={labels[theme]}
      aria-label={labels[theme]}
    >
      {icon}
      <span>{labels[theme]}</span>
    </button>
  )
}
